import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { adminStatus, requestAdminOtp, verifyAdminOtp } from "../../services/adminService";

const NAVY = "#2a2420";
const ORANGE = "#607840";

export default function AdminVerifyPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    adminStatus()
      .then(async (status) => {
        if (!mounted) return;
        if (!status.isPlatformAdmin) { navigate("/businesses", { replace: true }); return; }
        if (status.verified) { navigate("/admin", { replace: true }); return; }
        // Not yet verified — auto-request OTP (auto-fills and verifies instantly in dev)
        try {
          const result = await requestAdminOtp();
          if (!mounted) return;
          if (result.devCode) {
            await verifyAdminOtp(result.devCode);
            if (mounted) navigate("/admin", { replace: true });
          } else {
            setMessage(result.message || "Verification code sent. Check server logs.");
            setLoading(false);
            setSubmitting(false);
          }
        } catch (err) {
          if (mounted) {
            setError(err instanceof Error ? err.message : "Could not request verification code.");
            setLoading(false);
            setSubmitting(false);
          }
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message);
          setLoading(false);
          setSubmitting(false);
        }
      });
    return () => { mounted = false; };
  }, [navigate]);

  async function requestCode() {
    setError("");
    setMessage("");
    setSubmitting(true);
    try {
      const result = await requestAdminOtp();
      setMessage(result.message || "Verification code generated.");
      if (result.devCode) {
        setCode(result.devCode);
        await verifyAdminOtp(result.devCode);
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not request verification code.");
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await verifyAdminOtp(code);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify code.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fefcf8" }}>
        <Loader2 size={24} className="animate-spin" style={{ color: ORANGE }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#fefcf8" }}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-8" style={{ border: `1.5px solid ${NAVY}1a`, boxShadow: "0 4px 24px rgba(80,55,30,0.1)" }}>
        <div className="flex items-center gap-3 mb-6">
          <img src="/login-logo.png" alt="Bill Lens" className="w-10 h-10 rounded-xl object-cover" />
          <div>
            <p className="text-base font-extrabold" style={{ color: NAVY }}>Platform Admin</p>
            <p className="text-xs" style={{ color: NAVY, opacity: 0.5 }}>Extra verification required</p>
          </div>
        </div>

        <button
          type="button"
          onClick={requestCode}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60"
          style={{ backgroundColor: ORANGE }}
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          Request verification code
        </button>

        <form onSubmit={verify} className="mt-5 space-y-3">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="w-full rounded-lg px-3 py-2.5 text-center text-lg tracking-widest outline-none"
            style={{ border: `1.5px solid ${NAVY}22`, color: NAVY }}
          />
          <button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: NAVY }}
          >
            Verify and open admin
          </button>
        </form>

        {message && <p className="mt-4 text-xs" style={{ color: "#065f46" }}>{message}</p>}
        {error && <p className="mt-4 text-xs" style={{ color: "#991b1b" }}>{error}</p>}
        <p className="mt-4 text-[11px] leading-relaxed" style={{ color: NAVY, opacity: 0.45 }}>
          Use the latest code generated by the API server. For production, route this step through email, SMS, TOTP, or Okta.
        </p>
      </div>
    </div>
  );
}
